import React, { useCallback, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import to from 'await-to-js';
import { Alert, Button, DialogV2, FileUploadDropzone, MuiRouterLink } from 'c-components';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import ApiClient from 'c-data/apiClient';
import { NetworkRequestState } from 'c-data-layer';
import { useAPIClientRequest } from 'c-hooks';
import { PostAuthRoutes } from 'c-routes';
import { PermissionName } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import {
    buildOperationalHoursTemplate,
    OPERATIONAL_HOURS_TEMPLATE_FILENAME,
} from './operationalHoursTemplate';

// The API gates the upload on `admin`, like this page. The result is only shown in the task
// queue, so the button also needs the task queue page's own permission.
const operationalHoursPerms = [PermissionName.Admin, PermissionName.Pop_displaysJob_statusesRead];

interface OperationalHoursUploadDialogProps {
    dialogState: {
        value: boolean;
        setFalse: () => void;
        setTrue: () => void;
    };
}

const downloadTemplate = () => {
    const blob = new Blob([buildOperationalHoursTemplate()], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', OPERATIONAL_HOURS_TEMPLATE_FILENAME);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Bulk "Update operational hours" (UL-8199): download the template, upload a CSV. The API queues
 * it (ImportOperationalTimesCsv); the task queue entry carries the summary or every error.
 */
const OperationalHoursUploadDialog: React.FC<OperationalHoursUploadDialogProps> = ({
    dialogState,
}) => {
    const t = useCommonTranslation();
    const [file, setFile] = useState<File | null>(null);
    const { start, requestState, error, reset } = useAPIClientRequest(
        ApiClient.Entities.Displays_Screen.importOperationalTimes,
    );

    // Request state outlives the dialog: clear it on open so a reopened dialog starts fresh.
    const open = useCallback(() => {
        reset();
        setFile(null);
        dialogState.setTrue();
    }, [dialogState, reset]);

    const onFilesAdded = useCallback(
        (files: File[]) => {
            reset();
            setFile(files[0] ?? null);
        },
        [reset],
    );

    const upload = useCallback(async () => {
        // Upload is disabled without a file; the guard is for the type.
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        await to(start(formData));
    }, [file, start]);

    const inProgress = requestState === NetworkRequestState.InProgress;
    const queued = requestState === NetworkRequestState.Success;

    return (
        <IfHasAllPermissions permissions={operationalHoursPerms}>
            <DialogV2
                onClose={dialogState.setFalse}
                open={dialogState.value}
                title="Modules.Displays.DisplaysList.operationalHours.title"
            >
                <Stack gap={2}>
                    <Typography variant="body2">
                        {t('Modules.Displays.DisplaysList.operationalHours.replaces')}
                    </Typography>
                    <Typography variant="body2">
                        {t('Modules.Displays.DisplaysList.operationalHours.format')}
                    </Typography>
                    <Typography variant="body2">
                        {t('Modules.Displays.DisplaysList.operationalHours.allOrNothing')}
                    </Typography>
                    <Button onClick={downloadTemplate}>
                        {t('Modules.Displays.DisplaysList.operationalHours.downloadTemplate')}
                    </Button>
                    {queued ? (
                        <Alert severity="success">
                            <Stack gap={1}>
                                {t('Modules.Displays.DisplaysList.operationalHours.queued')}
                                <MuiRouterLink to={PostAuthRoutes.Admin.TaskQueue}>
                                    {t(
                                        'Modules.Displays.DisplaysList.operationalHours.openTaskQueue',
                                    )}
                                </MuiRouterLink>
                            </Stack>
                        </Alert>
                    ) : (
                        <>
                            <FileUploadDropzone
                                fileAccept=".csv"
                                onFilesAdded={onFilesAdded}
                                dropzoneLabel="Modules.Main.Campaigns.Overview.BookingSummary.dropzoneText"
                            />
                            {file && <Typography variant="body2">{file.name}</Typography>}
                            {requestState === NetworkRequestState.Error && (
                                <Alert severity="error">{String(error)}</Alert>
                            )}
                            <Button onClick={upload} disabled={!file || inProgress}>
                                {t('Modules.Displays.DisplaysList.operationalHours.upload')}
                            </Button>
                        </>
                    )}
                </Stack>
            </DialogV2>
            <Button
                size="small"
                sx={{
                    minHeight: 26,
                    minWidth: 0,
                    px: 1,
                    py: 0.3,
                    fontSize: '0.72rem',
                    lineHeight: 1.1,
                }}
                onClick={open}
            >
                {t('Modules.Displays.DisplaysList.operationalHours.button')}
            </Button>
        </IfHasAllPermissions>
    );
};

export default OperationalHoursUploadDialog;
